import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Alert,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
  Grid,
  Divider,
  Chip
} from '@mui/material';
import { 
  Language, 
  Download, 
  ContentCopy, 
  Category, 
  Inventory, 
  Article, 
  Refresh 
} from '@mui/icons-material';
import pb from './pocketbase';
import { saveAs } from 'file-saver';

const SitemapGenerator = () => {
  const [loading, setLoading] = useState(false);
  const [sitemap, setSitemap] = useState('');
  const [stats, setStats] = useState({
    products: 0,
    categories: 0,
    pages: 0,
    total: 0
  });
  const [domainUrl, setDomainUrl] = useState('https://goodmartbd.com');
  const [includeProducts, setIncludeProducts] = useState(true);
  const [includeCategories, setIncludeCategories] = useState(true);
  const [includePages, setIncludePages] = useState(true);
  const [lastGenerated, setLastGenerated] = useState(null);
  const [error, setError] = useState('');

  // Function to generate sitemap.xml
  const generateSitemap = async () => {
    try {
      setLoading(true);
      setError('');
      setSitemap('');
      
      // Initialize XML content
      let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
      xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
      
      // Track count of URLs
      let productsCount = 0;
      let categoriesCount = 0;
      let pagesCount = 0;
      
      // Add homepage
      xml += '  <url>\n';
      xml += `    <loc>${domainUrl}</loc>\n`;
      xml += '    <priority>1.0</priority>\n';
      xml += `    <lastmod>${new Date().toISOString().split('T')[0]}</lastmod>\n`;
      xml += '    <changefreq>daily</changefreq>\n';
      xml += '  </url>\n';
      
      // Add categories if enabled
      if (includeCategories) {
        try {
          const categoriesData = await pb.collection('categories').getFullList({
            sort: 'created',
          });
          
          for (const category of categoriesData) {
            xml += '  <url>\n';
            xml += `    <loc>${domainUrl}/category/${category.id}</loc>\n`;
            xml += '    <priority>0.8</priority>\n';
            xml += `    <lastmod>${new Date(category.updated || category.created).toISOString().split('T')[0]}</lastmod>\n`;
            xml += '    <changefreq>weekly</changefreq>\n';
            xml += '  </url>\n';
            
            categoriesCount++;
          }
        } catch (err) {
          console.error('Error fetching categories:', err);
          setError(prev => prev + (prev ? '\n' : '') + 'Error fetching categories: ' + err.message);
        }
      }
      
      // Add products if enabled
      if (includeProducts) {
        try {
          const productsData = await pb.collection('products').getFullList({
            sort: 'created',
            filter: 'status="published"'
          });
          
          for (const product of productsData) {
            // Use product slug if available, otherwise use ID
            const productIdentifier = product.slug || product.id;
            
            xml += '  <url>\n';
            xml += `    <loc>${domainUrl}/product/${productIdentifier}</loc>\n`;
            xml += '    <priority>0.6</priority>\n';
            xml += `    <lastmod>${new Date(product.updated || product.created).toISOString().split('T')[0]}</lastmod>\n`;
            xml += '    <changefreq>weekly</changefreq>\n';
            xml += '  </url>\n';
            
            productsCount++;
          }
        } catch (err) {
          console.error('Error fetching products:', err);
          setError(prev => prev + (prev ? '\n' : '') + 'Error fetching products: ' + err.message);
        }
      }
      
      // Add pages if enabled
      if (includePages) {
        try {
          // Try to fetch from 'pages' collection if it exists
          try {
            const pagesData = await pb.collection('pages').getFullList({
              sort: 'created'
            });
            
            for (const page of pagesData) {
              // Use page slug if available, otherwise use ID
              const pageIdentifier = page.slug || page.id;
              
              xml += '  <url>\n';
              xml += `    <loc>${domainUrl}/pages/${pageIdentifier}</loc>\n`;
              xml += '    <priority>0.7</priority>\n';
              xml += `    <lastmod>${new Date(page.updated || page.created).toISOString().split('T')[0]}</lastmod>\n`;
              xml += '    <changefreq>monthly</changefreq>\n';
              xml += '  </url>\n';
              
              pagesCount++;
            }
          } catch (err) {
            console.log('Pages collection might not exist:', err);
            
            // Add some common pages manually if no pages collection exists
            const commonPages = ['about', 'contact', 'shipping', 'returns', 'privacy-policy', 'terms-of-service', 'faq'];
            for (const page of commonPages) {
              xml += '  <url>\n';
              xml += `    <loc>${domainUrl}/pages/${page}</loc>\n`;
              xml += '    <priority>0.7</priority>\n';
              xml += `    <lastmod>${new Date().toISOString().split('T')[0]}</lastmod>\n`;
              xml += '    <changefreq>monthly</changefreq>\n';
              xml += '  </url>\n';
              
              pagesCount++;
            }
          }
        } catch (err) {
          console.error('Error fetching pages:', err);
          setError(prev => prev + (prev ? '\n' : '') + 'Error fetching pages: ' + err.message);
        }
      }
      
      // Close XML
      xml += '</urlset>';
      
      // Update state
      setSitemap(xml);
      setStats({
        products: productsCount,
        categories: categoriesCount,
        pages: pagesCount,
        total: productsCount + categoriesCount + pagesCount + 1 // +1 for homepage
      });
      setLastGenerated(new Date());
      
      // Automatically download the sitemap file
      const blob = new Blob([xml], { type: 'text/xml;charset=utf-8' });
      saveAs(blob, 'sitemap.xml');
      
    } catch (err) {
      console.error('Error generating sitemap:', err);
      setError('Error generating sitemap: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Function to download sitemap as XML file
  const downloadSitemap = () => {
    if (!sitemap) return;
    
    const blob = new Blob([sitemap], { type: 'text/xml;charset=utf-8' });
    saveAs(blob, 'sitemap.xml');
  };
  
  // Function to copy sitemap to clipboard
  const copySitemap = () => {
    if (!sitemap) return;
    navigator.clipboard.writeText(sitemap);
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Sitemap Generator
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Generate a sitemap.xml file for better search engine indexing of your website.
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Configuration
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Website URL"
                  value={domainUrl}
                  onChange={(e) => setDomainUrl(e.target.value)}
                  margin="normal"
                  helperText="Enter your website's domain without trailing slash"
                  InputProps={{
                    startAdornment: <Language sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Include in Sitemap:
                </Typography>
                
                <FormControl component="fieldset">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeProducts}
                        onChange={(e) => setIncludeProducts(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Inventory fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2">Products</Typography>
                      </Box>
                    }
                  />
                </FormControl>
                
                <FormControl component="fieldset">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeCategories}
                        onChange={(e) => setIncludeCategories(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Category fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2">Categories</Typography>
                      </Box>
                    }
                  />
                </FormControl>
                
                <FormControl component="fieldset">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includePages}
                        onChange={(e) => setIncludePages(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Article fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2">Pages</Typography>
                      </Box>
                    }
                  />
                </FormControl>
              </Box>
              
              <Button
                variant="contained"
                fullWidth
                onClick={generateSitemap}
                disabled={loading}
                type="button"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
                sx={{ mt: 2 }}
              >
                {loading ? 'Generating...' : 'Generate Sitemap'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Sitemap Preview
                </Typography>
                
                {sitemap && (
                  <Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={copySitemap}
                      sx={{ mr: 1 }}
                    >
                      Copy
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Download />}
                      onClick={downloadSitemap}
                    >
                      Download
                    </Button>
                  </Box>
                )}
              </Box>
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              {lastGenerated && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Last Generated: {lastGenerated.toLocaleString()}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    <Chip 
                      icon={<Inventory fontSize="small" />} 
                      label={`${stats.products} Products`} 
                      size="small" 
                      variant="outlined" 
                    />
                    <Chip 
                      icon={<Category fontSize="small" />} 
                      label={`${stats.categories} Categories`} 
                      size="small" 
                      variant="outlined" 
                    />
                    <Chip 
                      icon={<Article fontSize="small" />} 
                      label={`${stats.pages} Pages`} 
                      size="small" 
                      variant="outlined" 
                    />
                    <Chip 
                      label={`${stats.total} Total URLs`} 
                      size="small" 
                      color="primary" 
                    />
                  </Box>
                </Box>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              {sitemap ? (
                <Box 
                  sx={{
                    maxHeight: 400,
                    overflow: 'auto',
                    bgcolor: 'grey.100',
                    p: 2,
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all'
                  }}
                >
                  {sitemap}
                </Box>
              ) : (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: 200,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                  }}
                >
                  {loading ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Generating Sitemap...
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Click "Generate Sitemap" to create your sitemap.xml
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SitemapGenerator;